import {
  InputGroupType,
  OptionWithNote,
} from "@/models/form-types";
import { z } from "zod";

export const formSlugs = {
  // tech forms
  techElevate: "tech/elevate",
  techPitchElevateHer: "tech/celevateher",
  techPitchCBlack: "tech/cblack",
  techSpeaker: "tech/speaker",
  techInvestor: "tech/investor",
  techEngineers: "tech/engineers",
  techResponsibleDeepTech: "tech/responsible-deep-tech",
  // C-Tribe Startup Alpha 2027 (see startup-alpha-constants.ts).
  techStartupAlpha: "tech/startup-alpha",

  techExecLuncheonGeneralAdmission2k25: "tech/exec-luncheon/general-admission2k25",

  techCBlackSemiFinals: "tech/cblack-semi-finals",

  techCElevateHerSemiFinals: "tech/celevateher-semi-finals",

  // gaming forms
  gamingEsportOverwatch: "gaming/esports/overwatch",
  gamingSpeaker: "gaming/speaker",
  gamingDeveloper: "gaming/developer",
  gamingPitchQuest: "gaming/pitch-quest",
  gamingPlayTestZone: "gaming/playtest-zone",
  // CTJAM, the C-Tribe Game Jam (registration added 2026-11-04).
  gamingCtjam: "gaming/ctjam",

  // fashion forms
  fashionDesigner: "fashion/designer",
  fashionBuyer: "fashion/buyer",
  fashionStylist: "fashion/stylist",
  fashionMua: "fashion/mua",
  fashionModel: "fashion/model",
  // Persisted identity retained for the show rescheduled to February 25, 2027.
  fashionModelNovember2026: "fashion/model-november-2026",
  fashionDesignerFebruary2027: "fashion/designer-february-2027",
  fashionPhotographerFebruary2027: "fashion/photographer-february-2027",
  fashionProfessionals: "fashion/professionals",
  fashionExhibit: "fashion/exhibit",
  fashionPartner: "fashion/partner",

  // music forms
  musicArtist: "music/artist",
  musicPublisher: "music/publisher",
  musicPublisherPublisher: "music/publisher-publisher",

  // general forms
  vendor: "vendor",
  villageInterest: "village-interest",
  resumeUpload: "resumeupload",
  volunteer: "volunteer",
  mediaPress: "media-press",
  b2b: "b2b",
  influencer: "influencer",

  // photography forms
  photographer: "photographer",
  vipDinner: "vip-dinner",

  // diveristy pass
  diversityPass: "diversity-pass",
  generalSpeaker: "general/speaker",
  innovationZone: "innovation-zone",

  // cross-vertical forms
  partnership: "partnership",
  // Legacy single film form: kept so existing responses stay linked; the
  // page now renders a chooser pointing to the three forms below.
  film: "film",
  filmSubmission: "film/submission",
  filmPitch: "film/pitch",
  filmIndustry: "film/industry",
  artist: "artist",
  health: "health",

  // videographer/photographer
  videographer: "videographer",
} as const;

export const filmGenreOptions = [
  "Narrative Feature",
  "Narrative Short",
  "Documentary Feature",
  "Documentary Short",
  "Animation",
  "Experimental",
  "Music Video",
  "Web Series",
  "Other",
] as const;

export const artFormOptions = [
  "Visual Art (Painting, Sculpture, Mixed Media)",
  "Performance Art",
  "Installation Art",
  "Digital / New Media Art",
  "Photography",
  "Workshop Facilitation",
  "Other",
] as const;

export const partnershipTierOptions = [
  "Title Partner",
  "Presenting Partner",
  "Supporting Partner",
  "In-Kind Partner",
  "Not sure yet",
] as const;

// February 25, 2027 show photographer call
export const photographerFebAvailabilityOptions = [
  "Yes, the whole evening",
  "Part of the evening",
  "No",
] as const;

export const photographerFebCoverageOptions = [
  "Runway",
  "VIP+ dinner and guest portraits",
  "Photos with designers",
  "Backstage",
  "Marketplace",
  "Same-night picks for social media",
] as const;

export const photographerFebArrangementOptions = [
  "Paid coverage",
  "Media badge and photo credit",
  "Open to either",
] as const;

// CTJAM, the C-Tribe Game Jam (owner asked for registration, 2026-11-04).
// The two jams match gaming.ctribefestival.com/ctjam/.
export const ctjamJamOptions = [
  "Challenger Jam (a theme and judges, solo or teams of up to 5)",
  "Casual Jam (any team size, no theme)",
  "Not sure yet",
] as const;

export const ctjamTeamOptions = [
  "I'm registering a team",
  "I'm coming solo and want to join a team",
  "I'm coming solo and want to jam on my own",
] as const;

export const ctjamRoleOptions = [
  "Art and animation",
  "Programming",
  "Sound and music",
  "Writing and narrative",
  "Game design",
  "Production",
] as const;

export const ctjamExperienceOptions = [
  "This will be my first game jam",
  "I've done a few game jams",
  "I've done many jams or I make games for work",
] as const;


export const photographerFebRightsOptions = ["I agree"] as const;

export const videographerMediumOptions = [
  "Videography",
  "Photography",
  "Both",
] as const;

export const videographerContentStyleOptions = [
  "Long-form / Podcast-style",
  "Short-form / Reels / TikTok",
  "Event coverage / Highlight reels",
  "Cinematic / Narrative",
  "Documentary",
  "Fashion / Lookbook",
  "Live performance / Concert",
  "Corporate / Branded content",
  "Other",
] as const;

export const healthPracticeAreaOptions = [
  "Mental Health & Wellness",
  "Nutrition & Dietetics",
  "Fitness & Movement",
  "Traditional / Indigenous Healing",
  "Holistic Health",
  "Public Health & Community Health",
  "Health Technology",
  "Other",
] as const;

export const ERROR_MESSAGES = {
  FieldRequired: (fieldName?: string) =>
    `${!!fieldName ? fieldName : "Field"} is required`,
  MaxWords: (max: number) => `Maximum ${max} words allowed`,
  SelectOne: () => "Select One",
  MaxAllowed: (max: number) => `Maximum ${max} allowed`,
  MaxFileSize: (max: number) => `Maximum file size is ${max}MB`,
  AllowedFileExtentions: (exts: string[]) =>
    `${!!exts ? exts.join(", ") : ""} files are accepted`,
  AcceptTerms: () => `Must accept terms to proceed`,
  ValidEmail: () => `Please provide a valid email address`,
  ValidPhone: () => `Please enter a valid phone number, for example 780 555 0123. Outside Canada and the US, start with + and your country code.`,
  ValidUrl: () => `Please enter a full web address, for example https://www.yourwebsite.com`,
};

export const defaultRadioOptions = ["Yes", "No"] as const;

export const raceOptions: InputGroupType[] = [
  { name: "Asian", options: ["East Asian", "South Asian", "Southeast Asian"] },
  {
    name: "Native/Indigenous/Aboriginal",
    options: ["First Nations", "Métis", "Inuit"],
  },
  { name: "Middle Eastern/Arab", options: ["Middle Eastern", "Arab"] },
  {
    name: "Latin American/Hispanic",
    options: [
      "Black",
      "North African",
      "Central African",
      "South African",
      "West African",
      "Caribbean",
    ],
  },
  {
    name: "White/European",
    options: ["White", "North European", "East European", "West European"],
  },
  { name: "Other", options: ["Other", "Prefer Not Answer"] },
] as const;

const allRaceOptions = raceOptions.reduce((arr: string[], obj) => {
  arr.push(...obj.options);
  return arr;
}, [] as string[]);

export const zodRaceOptionsSchema = z.array(z.string()).refine(
  (val) => {
    return (
      Array.isArray(val) &&
      val.length > 0 &&
      val.every((entry) => allRaceOptions.some((opt) => opt == entry))
    );
  },
  {
    message: ERROR_MESSAGES.FieldRequired("Race"),
  }
);

export const genderOptions = [
  "Woman",
  "Man",
  "Transgender",
  "Gender Neutral",
  "Non-binary",
  "Agender",
  "Pangender",
  "Genderqueer",
  "Two-spirit",
  "Third Gender",
  "Other",
  "Prefer not to Answer",
] as const;

export const industryOptions = [
  "Agriculture",
  "Big Data",
  "Blockchain technology",
  "Consulting",
  "Cryptocurrency",
  "E-Commerce",
  "Education",
  "Energy, Environment, & Cleantech",
  "Entertainment, Media & Art",
  "Finance & Fintech",
  "Food & Beverage",
  "Gaming",
  "Healthcare & Biotechnology",
  "Human Resources",
  "Import/Export",
  "Information/Technology, SaaS",
  "Marketing, Branding & Advertising",
  "Metaverse",
  "Mobile & Wireless",
  "Natural Resources",
  "Real Estate",
  "Retail & Fashion",
  "Social Enterprise",
  "Social Media/Social Network",
  "Sports, Lifestyle, Fitness",
  "Transportation & Logistics",
  "Other",
] as const;

export const ventureDurationOptions = [
  "Less than 1 year",
  "1-2 years",
  "2-3 years",
  "3-5 years",
  "5+ years",
] as const;

export const audienceOptions = [
  "Beginner",
  "Intermediate",
  "Advanced",
] as const;

export const speakerTopicCategoryOptions = [
  "Artificial Intelligence & Machine Learning",
  "BioTech Breakthroughs",
  "Blockchain Terrain",
  "Cyber Secure",
  "Construction of Tomorrow",
  "Sustainable Shift",
  "Financial Frontier",
  "Q(uantum)-Revolution",
] as const;

export const fundFocusOptions = ["B2B", "B2C", "Both"] as const;

export const investmentStageOptions = [
  "Pre-seed",
  "Seed",
  "Series A",
  "Series B and beyond",
] as const;

export const investingExperienceOptions = [
  "Less than 1 year",
  "1-2 years",
  "2-3 years",
  "3-5 years",
  "5+ years",
] as const;

export const ageOptions = [
  "18 -24",
  "25 - 34",
  "35 - 44",
  "45 - 54",
  "55 - 64",
  "65+",
] as const;

export const contributeToEnvironmentOptions = [
  "Yes, we use innovative methods in manufacturing, wholesale, or agriculture that greatly lessen our environmental impact.",
  "Yes, our product or service helps protect, conserve, or restore the environment or nature resources.",
  "No, our company does not have an environmental focus.",
] as const;

export const companySizeOptions = [
  "1-10 employees",
  "11-50 employees",
  "51-200 employees",
  "201-500 employees",
  "501+ employees",
] as const;

export const companyInBusinessOptions = [
  "Less than 1 year",
  "1-3 years",
  "4-6 years",
  "7-10 years",
  "over 10 years",
] as const;

export const companyCategoryOptions = [
  "Black-Owned",
  "Minority-Owned",
  "Women-Owned",
  "Veteran-Owned",
  "LGBTQ-Owned",
  "N/A",
  "Other",
] as const;

export const vendorAcknowledgementOptions = [
  "I’m NOT a vendor selling/providing: Beverages of any kind ( not limited to packaged drinks that will or can be opened to enjoy on premises )",
  "I’m NOT a vendor selling/providing: Food or any kind ( not limited to packaged cookies, candies, sauces, cooked goods, etc. that will or can be opened to enjoy on premises )",
  "I’m NOT a vendor selling/providing: Vapes or any smoking products.",
] as const;

export const modelShoeSizeTypeOptions = ["Women's", "Men's"] as const;

export const numberOfLooksOptions = ["2-3", "4-6", "7+"] as const;

export const fashionPackageOptions = [
  "Free",
  "Friend",
  "Friend Partner Plus",
] as const;

// Runway packages, all-inclusive (owner direction 2026-10-20): each price
// covers the runway slot and a selling space on Runway Row (8 spaces, one per
// runway designer). CAD, no GST for now (owner 2026-10-20), paid only after acceptance. Stored values
// above stay the same; prices live in the notes. Spot counts stay internal.
const packageDetailsLink = (path: string) =>
  `Each price covers your runway slot and your space to sell. Applying is free and you only pay if you're accepted. If you aren't chosen for Free, we may offer you a Friend spot. [See everything each package includes](https://www.ctribefestival.com/join/${path}/#packages)`;

export const fashionPackageNotes = {
  february: [
    "**Free** ($0, chosen by our jury): up to 6 looks in a shared New Designers segment, plus a spot on a shared Debut Rail on Runway Row to sell your pieces.",
    "**Friend** ($595): up to 8 looks in your own slot, all edited runway photos and a short clip, plus your own Designer Rail on Runway Row.",
    "**Friend Partner Plus** ($1,595): up to 12 looks, first pick of opening or closing, a 30 to 60 second reel, plus a 10 x 10 ft Studio Booth on Runway Row.",
    packageDetailsLink("fashion-designers-feb"),
  ],
  august: [
    "**Free** ($0, chosen by our jury): up to 6 looks in a shared New Designers segment, plus a spot on a shared Debut Rail on Runway Row on your show day.",
    "**Friend** ($595 per show): up to 8 looks in your own slot, all edited runway photos and a short clip, plus your own Designer Rail on Runway Row on your show day.",
    "**Friend Partner Plus** ($1,895 per show): up to 12 looks, first pick of opening or closing, a 30 to 60 second reel, plus a 10 x 10 ft Studio Booth on Runway Row on your show day.",
    packageDetailsLink("fashion-designers"),
  ],
} as const;

export const fashionSelectionCategoryOptions = [
  "Men’s",
  "Women’s",
  "Unisex",
] as const;

export const newVendorOptions = ["First Year", "Returning"] as const;

export const businessTypeOptions = [
  "Retailer",
  "Agency",
  "Distributor",
  "Others",
] as const;

export const availabilityOptions = [
  "12 PM - 4 PM",
  "3 PM - 7 PM",
  "5 PM - 9 PM",
] as const;

export const gamingPitchQuestTeamOptions = [
  "Just me",
  "2-3",
  "4-6",
  "7+",
] as const;

export const gamingPitchQuestPlatformOptions = [
  "iOS",
  "Android",
  "Switch",
  "Playstation",
  "Xbox One",
  "PC",
  "Mac",
  "Linux",
  "VR/AR",
  "Blockchain",
  "Other",
] as const;

export const fashionModelAvailableDays = [
  "Thursday, August 12th",
  "Friday, August 13th",
  "Saturday, August 14th",
] as const;

export const fashionExhibitTypeOptions = [
  "Brand Showcase",
  "Pop-up Shop",
  "Art Installation",
  "Interactive Experience",
  "Other",
] as const;

// Indicative CAD (no GST for now). Owner-approved 2026-10-20; see vault
// fashion-marketplace-booth-research-2026-10.md.
export const fashionExhibitPackageOptions = [
  "Debut Rail (first-time designers, curated, limited spots): free",
  "Designer Rail: CAD 250 Feb 25 | CAD 195/day or 750 full run in August",
  "Community / Cultural Table: CAD 175 Feb 25 | CAD 150/day or 550 full run in August",
  "Market Table: CAD 395 Feb 25 | CAD 295/day or 1,100 full run in August",
  "10x10 Studio Booth: CAD 895 Feb 25 | CAD 650/day or 2,400 full run in August",
  "Brand Activation: CAD 2,500 to 5,000 Feb 25 | CAD 5,000 to 12,000 full run in August",
  "Drop Partner: from CAD 7,500 Feb 25 | from CAD 10,000 in August",
] as const;

export const fashionExhibitEventOptions = [
  "C-Tribe x Fashion Show (February 25, 2027)",
  "C-Tribe x Fashion events (August 2027)",
] as const;

export const fashionExhibitCategoryOptions = [
  "Emerging designer", "Streetwear", "Vintage", "Sneakers", "Accessories and jewellery",
  "Beauty", "Art and prints", "Cultural goods", "Other",
] as const;

export const fashionExhibitPriceRangeOptions = [
  "Under CAD 25", "CAD 25 to 75", "CAD 75 to 200", "CAD 200 to 500", "Over CAD 500",
] as const;

export const fashionExhibitResaleOptions = [
  "No, I sell my own designs or products",
  "Yes, some or all items are resale",
] as const;

export const fashionExhibitDropOptions = ["Yes", "Maybe", "No"] as const;

export const fashionPartnerTierOptions = [
  "Title Partner",
  "Presenting Partner",
  "Supporting Partner",
  "In-Kind Partner",
] as const;

export const fashionPartnerGoalOptions = [
  "Brand Visibility",
  "Community Engagement",
  "Talent Access",
  "Product Placement",
  "Media Coverage",
] as const;

export enum AreaOfInterest {
  GAMING = "GAMING",
  TECH = "TECH",
  CULTURE = "CULTURE",
  MUSIC = "MUSIC",
  FASHION = "FASHION",
  FILM = "FILM",
}

export const areaOfInterestOptionsWithNote: OptionWithNote<
  keyof typeof AreaOfInterest
>[] = [
  {
    opt: "GAMING",
    note: "C-Tribe provides gamers and gaming industry professionals, particularly those from underrepresented communities, with access to industry training, professional development, and a unique environment for exploring the gaming industry.",
  },
  {
    opt: "TECH",
    note: "C-Tribe x Tech brings together engineers, VCs, entrepreneurs, recruiters, technologists, and culture enthusiasts to help accelerate their tech-focused careers and ventures.",
  },
  {
    opt: "CULTURE",
    note: "C-Tribe x Culture is an immersive and interactive gathering that includes cultural activations, food tours, a marketplace, art installations, and a one-of-a-kind experience.",
  },
  {
    opt: "MUSIC",
    note: "C-Tribe x Music Festival is an immersive and interactive gathering that brings together music lovers and festival-goers for a series of performances meant to empower the next generation of emerging musicians.",
  },
  {
    opt: "FASHION",
    note: "C-Tribe x Fashion stands at the forefront of influencing culture, setting trends, and steering the course of global fashion evolution.",
  },
  {
    opt: "FILM",
    note: "C-Tribe x Film will be a platform where the intersection art and cinema gives rise to captivating narratives and unique perspectives.",
  },
] as const;

export const organizationTypeOptions = [
  "Startup (1-50 employees)",
  "Small Enterprise (51-200 employees)",
  "Medium Enterprise (201-500 employees)",
  "Large Enterprise (500+ employees)",
] as const;

export const activityAreasOptions: InputGroupType[] = [
  {
    name: "Company Services",
    options: [
      "Developer/ Distributor of software solutions",
      "Information technologies services",
      "App developer",
      "Video game developer",
      "Consulting",
      "Telecommunication services",
      "Information and content provider",
      "Hardware manufacturer / distributor",
      "Marketing",
      "Security/ Identity management",
    ],
  },
  {
    name: "Technology",
    options: [
      "IOT/ M2M",
      "Big Data",
      "Design/ User Interface/ UX",
      "Artificial Intelligence/ Machine Learning",
      "Cloud computing",
      "GIS geographical information system",
      "Open source",
      "Wearables/ sensors",
      "Data Protection/ Data security/ cryptography",
      "Identity management",
      "Virtual Reality",
      "Augmented Reality",
      "Connected Car",
      "NFC/ iBeacons",
      "WAN/ MAW/ LAN/ Wifi",
      "Bluetooth",
      "RFID",
      "SG",
      "LTE/ Wimax",
      "Blockchain",
      "Robotics",
      "Drones",
      "Batteries",
      "PAN ( Personal Area Network )",
    ],
  },
  {
    name: "Main Industry Clients",
    options: [
      "Logistics",
      "Mobility",
      "Smart Cities",
      "Retail/ e-commerce",
      "Digital marketing",
      "Public services/ eGovernment",
      "Industry 4.0",
      "Health/ ageing/ beauty",
      "Connected vehicle",
      "Education",
      "Energy, water and environmental resources",
      "M-banking/ m-wallet/ Fintech",
      "Gaming, entertainment and leisure",
      "Multimedia",
      "Tourism",
      "Cultural and creative industries",
      "Food industry",
      "Sports",
      "Design industries",
      "Telecom operators",
    ],
  },
];

const allActivityAreasOptions = activityAreasOptions.reduce(
  (arr: string[], obj) => {
    arr.push(...obj.options);
    return arr;
  },
  [] as string[]
);

export const zodActivityAreasOptionsSchema = z.array(z.string()).refine(
  (val) => {
    return (
      Array.isArray(val) &&
      val.length > 0 &&
      val.every((entry) => allActivityAreasOptions.some((opt) => opt == entry))
    );
  },
  {
    message: ERROR_MESSAGES.FieldRequired("Activity Areas"),
  }
);

export const b2bPrimaryGoalOptions = [
  "Product Development and Innovation",
  "Market Expansion and Growth",
  "Cost Reduction and Efficiency Improvement",
  "Access to New Technologies",
  "Enhancing Customer Experience",
  "Improving Operational Processes",
  "Increasing Sales and Revenue",
  "Strengthening Brand and Market Presence",
  "Gaining Regulatory and Compliance Support",
  "Access to Specialized Knowledge or Expertise",
  "Enhancing Supply Chain and Distribution Networks",
  "Sustainability and Environmental Goals",
  "Risk Management and Mitigation",
  "Other",
] as const;

export const b2bSessionOptions = [
  "Thursday, August 12th - 12 PM - 2 PM",
  "Thursday, August 12th - 2 PM - 4 PM",
  "Friday, August 13th - 12 PM - 2 PM",
  "Friday, August 13th 2 PM - 4 PM",
] as const;

export const musicPubisherGenreOptions = [
  "Pop",
  "Rock",
  "Hip Hop",
  "R&B",
  "Jazz",
  "Classical",
  "Country",
  "Electronic",
  "Other",
] as const;

export const musicPublisherActiveYearsOptions = [
  "Less than 1 year",
  "1-3 years",
  "4-7 years",
  "7+ years",
] as const;

export const musicPublisherCollaborationTypeOptions = [
  "Co-writing (partners to write new material together)",
  "Production deals (producers to work on tracks or full albums)",
  "Recording contracts (interest in signing with a label for recording and releasing music)",
  "Tour support (seeking support for booking, planning and managing tours)",
  "Sync licensing (interesting in licensing music for film, TV, commercials, video games etc.)",
  "Management and representation (looking for managers or agents to represent and promote careers)",
  "Remix opportunities (seeking DJs or producers to remix existing tracks)",
  "Song placement (aiming to place songs with other artists)",
  "Merchandising (looking for partners to help with creating and selling merchandise)",
  "Co-publishing deals (interested in sharing rights and revenue with another publisher)",
  "Artist development (offering services to help develop and nurture talent)",
] as const;

export const musicPublisherPubisherSoughtTypeOptions = [
  "Lyricists",
  "Composers",
  "Instrumentalists",
  "Vocalists",
  "Producers",
  "Beatmakers",
  "Session musicians",
  "Sound Engineers",
  "DJ",
  "Remix Artists",
  "Other",
] as const;

export const generalSpeakerTopicCategoryOptions = [
  "C-Tribe x Tech",
  "C-Tribe x Music",
  "C-Tribe x Culture",
  "C-Tribe x Fashion",
  "C-Tribe x Film",
  "C-Tribe x Gaming",
] as const;
